<template>
  <article>
    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content" :class="`shift-for-capitals ${isMobile ? 'tablet' : ''}`">
        <aside>
          <figure class="image">
            <img alt="Main image" :src="currentRound?.answerImage?.image?.src" />
          </figure>
        </aside>
        <text-display-list :isMobileView="isMobile">
          <li class="step">
            <blinking-cursor-text
              :text="currentRound?.answer"
              :roundId="progress.roundIndex"
              :text-step="0"
            ></blinking-cursor-text>
          </li>
        </text-display-list>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import ErrorLogger from 'mixins/ErrorLogger';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';

export default {
  components: {
    ActivityStage,
    BlinkingCursorText,
    TextDisplayList
  },
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],
  data() {
    return {
      activityName: 'ShiftForCapitals'
    };
  },
  beforeDestroy() {
    TweenMax.killAll();
  },
  computed: {
    isMobile() {
      return this.device.isMobile;
    }
  },
  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
      await this.loadImages();
    },

    async loadImages() {
      this.rounds.forEach(round => this.queueImage(round.answerImage?.image?.src));

      return this.downloadImages();
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          written: 'Type each sentence.',
          voice: `${this.voiceInstructionsPrefix}/shiftforcapitals-${this.getRandomVoice()}-1.mp3`
        }
      };
    },

    startActivity() {
      const timeline = new TimelineLite();

      timeline
        .call(() => this.setupNewRound({ stepCallback: this.stepCallback }))
        .call(this.enableKeyboard)
        .call(this.errorLoggerStart)
        .to('.shift-for-capitals', 1, { opacity: 1 });

      this.$nextTick(this.setActivityContainerHeight);
    },

    stepCallback(rootGetters) {
      return rootGetters['activity/currentRound']['answer'];
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      this.sndBell();
      this.finishRound();
    },

    finishRound() {
      if (this.isEndOfActivity) {
        const timeline = new TimelineLite();
        timeline.delay(1).call(this.completeActivity);
        return;
      }
      this.completeStep();
      this.setCurrentStep();
      this.setupNewRound({ stepCallback: this.stepCallback });
    }
  }
};
</script>

<style lang="scss" scoped>
.shift-for-capitals {
  opacity: 0;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  text-align: center;
  position: relative;

  figure {
    height: 175px;
    position: relative;

    img {
      height: 100%;
    }
  }

  ul {
    list-style: none;
    font-size: 34px;
    -webkit-padding-start: 0;
  }
}

.is-chrome-ipad {
  .activity-stage {
    justify-content: start;
  }
}
</style>
